/**
 * This file provides query API interactions.
 */
import HTTPRetryUtil from 'src/utils/httpretry';
import Vue from 'vue';
import dayjs from 'dayjs';
import { expandQuery, hasUnstructured } from 'src/utils/query';
import { calculateDateWithUTCOffset } from 'src/utils/dates';
export function expandQueryIfPossible(query, savedQueries) {
    if (savedQueries) {
        query = expandQuery('_query', query, savedQueries);
    }
    return query;
}
// This function is identical in it's function to `expandQueryIfPossible`,
// and it's only puprpose if to avoid ts errors by using compatible typed
// parameters and return type. The above function was not overwritten for the
// sake of legacy compatibility.
export function expandSavedQueryIfPossible(queryName, query, savedQueries) {
    if (savedQueries) {
        query = expandQuery(queryName, query, savedQueries);
    }
    return query;
}
export default {
    /**
     * Execute a query against the topic model. This will return frames (or documents) with actual verbatims.
     * Does not require nested queries to be expanded, botanic handles it
     */
    runQuery(projectId, analysisId, indexQuery, savedQueries, { start = 0, limit = 10, documents = false, networkModel = false, numNetworkConcepts = undefined, sortOrder = 'most_relevant', }) {
        let urlStr = `projects/${projectId}/analysis/${analysisId}/query/`;
        let params = {
            documents,
            network_model: networkModel,
            sort_order: sortOrder,
            start,
            limit,
        };
        if (numNetworkConcepts !== undefined)
            params.num_network_concepts = numNetworkConcepts;
        indexQuery = expandQueryIfPossible(indexQuery, savedQueries);
        return Vue.http.post(urlStr, JSON.stringify(indexQuery), { params })
            .then((response) => Promise.resolve(response.json()), (error) => Promise.reject(error));
    },
    /**
     * Api function that contacts the unmapped endpoint in chrysalis through the tunnel in botanic.
     * This particular call is only intended to be used for the new qeiii and should only be used
     * behind the qeiii feature flag until it becomes GA.
     * It is used for the context network, concepts and unmappedExcerpts widget on the unmapped screen.
     */
    runUnmapped(projectId, body, topic_id, chrysalisUrl, chrysalisRef, { start = 0, limit = 10, documents = false, networkModel = false, numNetworkConcepts = undefined, sortOrder = 'most_relevant', }) {
        const urlStr = `projects/${projectId}/tunnel${chrysalisRef}/_topics/${topic_id}/_unmapped/`;
        let params = {
            documents,
            generate_network_model_v2: networkModel,
            sort_order: sortOrder,
            start,
            limit,
            chrysalis_url: chrysalisUrl,
            chrysalisRef: chrysalisRef,
            invalidate_cache: false,
            // We don't invalidate the chrysalis cache when new themes are saved,
            // so we can't make use of it here.
            allow_read_cache: false,
        };
        if (numNetworkConcepts !== undefined)
            params.num_network_concepts = numNetworkConcepts;
        return Vue.http.post(urlStr, JSON.stringify(body), { params })
            .then((response) => Promise.resolve(response.json()), (error) => Promise.reject(error));
    },
    scanRadar(projectId, analysisId) {
        let urlStr = `projects/${projectId}/analysis/${analysisId}/radar/`;
        return Vue.http.get(urlStr)
            .then((response) => Promise.resolve(response.json()), (error) => Promise.reject(error));
    },
    clearRadar(projectId, analysisId) {
        let urlStr = `projects/${projectId}/analysis/${analysisId}/clear-radar/`;
        return Vue.http.put(urlStr)
            .then((response) => Promise.resolve(response.json()), (error) => Promise.reject(error));
    },
    /**
     * Execute a query against the topic model. This will return frames (or documents) with actual verbatims.
     */
    async runQueryExport(projectId, analysisId, indexQuery, savedQueries) {
        indexQuery = expandQueryIfPossible(indexQuery, savedQueries);
        const res = await Vue.http.post(`projects/${projectId}/analysis/${analysisId}/query_export/`, JSON.stringify(indexQuery));
        return await res.json();
    },
    /**
     * Execute a drilldown (multi-segment query) against a topic model.
     */
    drilldown(projectId, analysisId, query, dataType, returnDocuments = 0, savedQueries) {
        const params = {
            data_type: dataType,
            return_documents: returnDocuments,
        };
        query = expandQueryIfPossible(query, savedQueries);
        return Vue.http.post(`projects/${projectId}/analysis/${analysisId}/drilldown/`, JSON.stringify(query), { params })
            .then((response) => Promise.resolve(response.json()), (error) => Promise.reject(error));
    },
    /**
     * Get relevant NPS data (promoters, detractors, passives) from a specified analysis
     */
    getNpsData(projectId, analysisId, query = {}, savedQueries) {
        query = expandQueryIfPossible(query, savedQueries);
        query = JSON.stringify(query);
        let requests = [];
        let types = ['promoters', 'passives', 'detractors'];
        for (let npsType of types) {
            requests.push(Vue.http.post(`projects/${projectId}/analysis/${analysisId}/nps-data/`, query, { params: { type: npsType } })
                .then((response) => Promise.resolve(response.json()), (error) => Promise.reject(error)));
        }
        return Promise.all(requests).then((values) => {
            let result = {};
            types.forEach((npsType, i) => {
                result[npsType] = values[i];
            });
            return result;
        });
    },
    /**
     * Query chrysalis for trend data
     */
    trend(projectId, analysisId, query, resolution, documents = true, savedQueries) {
        const params = {
            resolution,
            documents,
        };
        query = expandQueryIfPossible(query, savedQueries);
        return Vue.http.post(`projects/${projectId}/analysis/${analysisId}/trend/`, JSON.stringify(query), { params })
            .then(response => {
            return response.json();
        })
            .then(returnVal => {
            // Always convert our datetimes to JS objects using dayjs. Do this for all fields.
            Object.values(returnVal).forEach((value) => {
                value.datetimes = value.datetimes.map((dt) => {
                    // Get milliseconds since epoch from dayjs object
                    const dateInMilliseconds = dayjs(dt).valueOf();
                    return calculateDateWithUTCOffset(dateInMilliseconds);
                });
            });
            return returnVal;
        }, (error) => Promise.reject(error));
    },
    /**
     * Create a new saved query
     */
    createSavedQuery(projectId, analysisId, query, dashboard) {
        return Vue.http.post(`projects/${projectId}/analysis/${analysisId}/saved-query/`, query, { params: { dashboard } })
            .then((response) => Promise.resolve(response), (error) => Promise.reject(error));
    },
    /**
     * Create a new saved query
     */
    createSavedQueryV2(projectId, analysisId, body) {
        return HTTPRetryUtil.post(`projects/${projectId}/analysis/${analysisId}/v2/saved-query/`, {
            body: JSON.stringify(body),
            config: {
                params: {
                    invalidate_cache: false,
                }
            }
        });
    },
    /**
     * Delete a saved query by specified query id
     */
    async deleteSavedQuery(projectId, analysisId, queryId) {
        let url = `projects/${projectId}/analysis/${analysisId}/saved-query/${queryId}/`;
        return await Vue.http.delete(url);
    },
    /**
     * List saved queries attached to an analysis
     */
    async listSavedQueries(projectId, analysisId) {
        let url = `projects/${projectId}/analysis/${analysisId}/saved-query/`;
        let response = await Vue.http.get(url);
        return await response.json();
    },
    /**
     * Update a saved query
     */
    async updateSavedQuery(projectId, analysisId, query) {
        return await HTTPRetryUtil.put(`projects/${projectId}/analysis/${analysisId}/saved-query/${query.id}/`, JSON.stringify(query), {
            params: {
                invalidate_cache: false,
            }
        });
    },
    /**
     * Update a saved query
     */
    async updateSavedQueryV2(projectId, analysisId, query) {
        return await HTTPRetryUtil.put(`projects/${projectId}/analysis/${analysisId}/v2/saved-query/${query.id}/`, JSON.stringify(query), {
            params: {
                invalidate_cache: false,
            }
        });
    },
    /**
     * Generate a context network model.
     */
    async generateContextNetwork(projectId, analysisId, chrysalisUrl, chrysalisRef, topicId, indexQuery, savedQueries, networkConfig) {
        const expandedQuery = expandQuery('_query', indexQuery, savedQueries);
        const urlStr = `projects/${projectId}/tunnel${chrysalisRef}/_topics/${topicId}/_context_network/`;
        return await HTTPRetryUtil.post(urlStr, {
            body: {
                query: expandedQuery,
                baseline_query: networkConfig.baseline_query,
                num_network_concepts: networkConfig.num_network_concepts,
                network_cooccurrence_boost: networkConfig.network_cooccurrence_boost
            },
            config: {
                params: {
                    invalidate_cache: false,
                    topicId: topicId,
                    chrysalis_url: chrysalisUrl,
                    chrysalisRef: chrysalisRef,
                    allow_read_cache: true,
                }
            }
        });
    },
    async getThemeStats(projectId, savedQueries, topic_id, chrysalisUrl, chrysalisRef) {
        const expandedQueries = savedQueries.map(q => {
            const expandedValue = expandQuery(q.name, q.query_value, savedQueries);
            return {
                name: q.name,
                omit_from_overall: !hasUnstructured(expandedValue),
                value: expandedValue,
            };
        });
        const urlStr = `projects/${projectId}/tunnel${chrysalisRef}/_topics/${topic_id}/_query_stats/`;
        return await HTTPRetryUtil.post(urlStr, {
            body: {
                queries: expandedQueries,
            },
            config: {
                params: {
                    invalidate_cache: false,
                    topicId: topic_id,
                    chrysalis_url: chrysalisUrl,
                    chrysalisRef: chrysalisRef,
                    // This must be false because we currently don't invalidate the
                    // chrysalis read cache (or the appropriate section of the cache)
                    // when new themes are added. If that changes, we can set this to true.
                    allow_read_cache: false,
                }
            }
        });
    },
    async getThemeStatsV2(projectId, groupTree, topic_id, chrysalisUrl, chrysalisRef) {
        const urlStr = `projects/${projectId}/tunnel${chrysalisRef}/_topics/${topic_id}/_query_stats/v2/`;
        return await HTTPRetryUtil.post(urlStr, {
            body: {
                group_tree: groupTree,
            },
            config: {
                params: {
                    invalidate_cache: false,
                    topicId: topic_id,
                    chrysalis_url: chrysalisUrl,
                    chrysalisRef: chrysalisRef,
                    // This must be false because we currently don't invalidate the
                    // chrysalis read cache (or the appropriate section of the cache)
                    // when new themes are added. If that changes, we can set this to true.
                    allow_read_cache: false,
                }
            }
        });
    },
    /**
     * Get Variants of Text Values in a Query.
     */
    async getQueryTextVariants(projectId, chrysalisUrl, chrysalisRef, topicId, indexQuery, savedQueries) {
        const expandedQuery = expandQuery('_query', indexQuery, savedQueries);
        const urlStr = `projects/${projectId}/tunnel${chrysalisRef}/_topics/${topicId}/_variants/`;
        return await HTTPRetryUtil.post(urlStr, {
            body: expandedQuery,
            config: {
                params: {
                    invalidate_cache: false,
                    topicId: topicId,
                    chrysalis_url: chrysalisUrl,
                    chrysalisRef: chrysalisRef,
                    allow_read_cache: true,
                }
            }
        });
    },
    /**
    * Generate a summarization from text
    */
    async generateSummary(projectId, chrysalisUrl, chrysalisRef, topicId, indexQuery, savedQueries, summaryType, themeName, sampleVerbatims) {
        let expandedQuery = expandQuery('_query', indexQuery, savedQueries);
        expandedQuery = [{
                name: themeName,
                value: expandedQuery
            }];
        const urlStr = `projects/${projectId}/tunnel${chrysalisRef}/_topics/${topicId}/summarize/`;
        return await HTTPRetryUtil.post(urlStr, {
            count: 1,
            body: {
                queries: expandedQuery,
                summary_type: summaryType,
                sample_verbatims: sampleVerbatims,
            },
            config: {
                params: {
                    invalidate_cache: false,
                    topicId: topicId,
                    chrysalis_url: chrysalisUrl,
                    chrysalisRef: chrysalisRef,
                    allow_read_cache: true,
                }
            }
        });
    },
};
export const ThemeGroup = {
    async list(projectId, analysisId) {
        const url = `projects/${projectId}/analysis/${analysisId}/theme_groups/`;
        return Vue.http.get(url).then((res) => res.json());
    },
    async create(projectId, analysisId, payload) {
        const url = `projects/${projectId}/analysis/${analysisId}/theme_groups/`;
        return Vue.http.post(url, payload).then((res) => res.json());
    },
    async update(projectId, analysisId, groupId, data) {
        const url = `projects/${projectId}/analysis/${analysisId}/theme_groups/`;
        return Vue.http.put(url, { id: groupId, ...data }).then((res) => res.json());
    },
    async delete(projectId, analysisId, groupId) {
        const url = `projects/${projectId}/analysis/${analysisId}/theme_groups/${groupId}`;
        return Vue.http.delete(url).then((res) => res.json());
    },
    async updateGroupParent(projectId, analysisId, childId, parentId) {
        return ThemeGroup.update(projectId, analysisId, childId, { parent_group: parentId });
    },
    async updateThemeParent(projectId, analysisId, childId, parentId) {
        const url = `projects/${projectId}/analysis/${analysisId}/saved-query/theme_group/`;
        return Vue.http.put(url, { id: childId, theme_group: parentId }).then((res) => res.json());
    },
};
